import { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { 
  BookOpen, Plus, Send, CheckCircle2, MessageCircle, 
  ChevronRight, ChevronLeft, Sparkles, Users, BarChart3,
  FileText, Image, Video, Share2
} from "lucide-react";

const steps = [
  {
    id: 1,
    title: "Benvenuto in GuideFlow! 👋",
    subtitle: "La piattaforma per creare e condividere guide interattive",
    description: "GuideFlow ti permette di creare guide passo-passo per qualsiasi scopo: formazione clienti, procedure aziendali, tutorial prodotti, e molto altro.",
    icon: Sparkles,
    color: "from-blue-500 to-indigo-600",
    tips: [
      "Crea guide visive con immagini e video",
      "Condividi istantaneamente via WhatsApp",
      "Monitora chi ha completato le guide",
      "Perfetto per assistenza clienti e formazione"
    ]
  },
  {
    id: 2,
    title: "1. Crea un Modello",
    subtitle: "Il modello rappresenta il tuo prodotto, servizio o procedura",
    description: "Ogni modello può avere una guida associata. Ad esempio: un modello di cassa, un macchinario, una procedura aziendale, un corso di formazione.",
    icon: Plus,
    color: "from-emerald-500 to-teal-600",
    tips: [
      "Clicca \"+ Nuovo\" nella dashboard",
      "Dai un nome descrittivo (es: \"Cassa XYZ\")",
      "Assegna un codice univoco (es: \"CASSA_XYZ\")",
      "Il codice verrà usato nel link della guida"
    ]
  },
  {
    id: 3,
    title: "2. Costruisci la Guida",
    subtitle: "Aggiungi i passi con testo, immagini e video",
    description: "Entra nel modello e costruisci la guida passo dopo passo. Ogni passo può avere un titolo, una descrizione, immagini esplicative e video tutorial.",
    icon: FileText,
    color: "from-purple-500 to-pink-600",
    tips: [
      "Clicca \"Modifica\" sul modello",
      "Aggiungi passi con \"+ Aggiungi passo\"",
      "Carica immagini per illustrare ogni passaggio",
      "Usa i tipi: Normale, Attenzione, Suggerimento",
      "Trascina i passi per riordinarli"
    ]
  },
  {
    id: 4,
    title: "3. Pubblica e Condividi",
    subtitle: "Invia la guida ai tuoi clienti via WhatsApp",
    description: "Una volta completata la guida, attiva la pubblicazione e condividila. Il cliente riceverà un link che apre la guida in formato mobile-friendly.",
    icon: Share2,
    color: "from-green-500 to-emerald-600",
    tips: [
      "Attiva l'interruttore \"Pubblicata\"",
      "Clicca il pulsante verde WhatsApp",
      "Inserisci nome e numero del cliente",
      "Il messaggio con il link viene preparato automaticamente",
      "Il cliente apre la guida dal suo telefono"
    ]
  },
  {
    id: 5,
    title: "4. Monitora i Completamenti",
    subtitle: "Verifica chi ha completato le guide",
    description: "Quando un cliente arriva alla fine della guida e clicca \"Conferma completamento\", riceverai una notifica nella dashboard con un badge verde.",
    icon: BarChart3,
    color: "from-orange-500 to-red-600",
    tips: [
      "Controlla la sezione \"Log Invii Guide\"",
      "Vedi chi ha ricevuto quale guida",
      "Il badge \"Confermato\" indica completamento",
      "Le statistiche in alto mostrano i totali",
      "I dati si aggiornano automaticamente"
    ]
  },
  {
    id: 6,
    title: "Esempi di Utilizzo",
    subtitle: "GuideFlow si adatta a qualsiasi settore",
    description: "La piattaforma è versatile e può essere utilizzata in molteplici contesti lavorativi:",
    icon: Users,
    color: "from-cyan-500 to-blue-600",
    tips: [
      "🛒 Retail: Guide per utilizzo registratori di cassa",
      "🏭 Industria: Procedure di manutenzione macchinari",
      "🏥 Sanità: Istruzioni per dispositivi medici",
      "📚 Formazione: Corsi e tutorial interattivi",
      "🔧 Assistenza: Troubleshooting e risoluzione problemi",
      "🏢 HR: Onboarding nuovi dipendenti"
    ]
  }
];

export default function OnboardingWizard({ open, onClose }) {
  const [currentStep, setCurrentStep] = useState(0);
  
  const step = steps[currentStep];
  const isFirst = currentStep === 0;
  const isLast = currentStep === steps.length - 1;
  const Icon = step.icon;

  const handleNext = () => {
    if (isLast) {
      onClose();
      setCurrentStep(0);
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirst) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleClose = () => {
    onClose();
    setCurrentStep(0);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-xl p-0 overflow-hidden">
        {/* Header with gradient */}
        <div className={`bg-gradient-to-r ${step.color} p-6 text-white`}>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center">
              <Icon className="w-7 h-7" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold">{step.title}</h2>
              <p className="text-white/80 text-sm mt-1">{step.subtitle}</p>
            </div>
          </div>
          
          {/* Progress dots */}
          <div className="flex gap-2 mt-6">
            {steps.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentStep(idx)}
                className={`h-2 rounded-full transition-all ${
                  idx === currentStep 
                    ? 'w-8 bg-white' 
                    : idx < currentStep 
                      ? 'w-2 bg-white/60' 
                      : 'w-2 bg-white/30'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-muted-foreground mb-4">
            {step.description}
          </p>
          
          <div className="space-y-2">
            {step.tips.map((tip, idx) => (
              <div 
                key={idx} 
                className="flex items-start gap-3 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
              >
                <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                <span className="text-sm">{tip}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t bg-muted/30">
          <Button
            variant="ghost"
            onClick={handlePrev}
            disabled={isFirst}
            className="gap-2"
          >
            <ChevronLeft className="w-4 h-4" />
            Indietro
          </Button>
          
          <span className="text-sm text-muted-foreground">
            {currentStep + 1} di {steps.length}
          </span>
          
          <Button
            onClick={handleNext}
            className="gap-2"
          >
            {isLast ? (
              <>
                Inizia
                <Sparkles className="w-4 h-4" />
              </>
            ) : (
              <>
                Avanti
                <ChevronRight className="w-4 h-4" />
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
